// Porte unique du formatage des prix (Phase 95).
//
// Ce module est à `Intl.NumberFormat` ce que `parisTime.ts` est à
// `Intl.DateTimeFormat` : le seul endroit où un montant devient du texte.
//
// Ne JAMAIS assembler un prix à la main. Trois pièges par langue que seul ICU
// connaît : le séparateur décimal (`45,00` vs `45.00`), la position du symbole
// (`45,00 €` vs `€45.00`) et l'espace insécable étroit qui précède le « € » en
// français. Une concaténation « symbole + nombre » produit `€45,00` en
// français — faux, et faux d'une façon que personne ne relit.
//
// L'entrée est en CENTIMES ENTIERS (la forme dans laquelle Stripe et la base
// stockent les montants) : la division par 100 arrive au dernier moment, juste
// avant l'affichage, donc aucune addition d'euros flottants en amont.
//
// DÉCIMALES : un montant rond s'affiche SANS décimales (`10 €`, `€10`), un
// montant à centimes les montre EN ENTIER (`10,50 €`, `€10.50`). Le défaut
// d'ICU pour une devise est `minimumFractionDigits: 2`, qui aurait transformé
// « Payer (10 €) » en « Payer (10,00 €) » sur le site français — une phase de
// traduction n'a pas à changer ce que voient les visiteurs francophones déjà
// là. Décision opérateur du 2026-08-16.
//
// Le choix se fait PAR MONTANT, jamais globalement. Un `minimumFractionDigits: 0`
// posé une fois pour toutes supprime aussi le zéro final : 10,50 € devient
// « 10,5 € », ce qui n'est pas un prix. C'est le test qui l'a attrapé.
// Refs: phase 95.
import { INTL_LOCALE } from './parisTime'
import type { Lang } from '../i18n/types'

// Montants AFFICHÉS, en centimes entiers. Ils ne décident de RIEN : le prix
// réellement encaissé vient du serveur (slug → Edge Function). Ils vivent ici,
// à côté de la porte qui les met en forme, parce qu'ils étaient déclarés à
// l'identique dans Home.tsx ET Dashboard.tsx (Phase 95) et que la Phase 97 en
// ajoutait deux copies de plus sur les pages d'offre. Quatre déclarations du
// même nombre, c'est une divergence qui attend son heure.
// Refs: phase 97.
export const SESSION_PRICE_CENTS = 2500
export const TRIAL_PRICE_CENTS = 1000
export const COACHING_PRICE_CENTS = 8000

export function formatEuros(cents: number, lang: Lang): string {
  // Montant rond → 0 décimale ; le moindre centime → 2, jamais 1.
  const digits = cents % 100 === 0 ? 0 : 2
  return new Intl.NumberFormat(INTL_LOCALE[lang], {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(cents / 100)
}
